import React, { useState, useEffect, useRef } from 'react';

import { ThemeProvider } from '@mui/material/styles';
import theme from './style/theme';
import "./style/style.css";
import { Toast } from './types/dataTypes';
import CustomToast from './common/components/CustomToast';
import useOnlineStatus from './utils/useOnline';
import { ToastContext } from './utils/context/toastContext';
import Layout from './features/campaign-creation/Layout';
import Listing from './features/campaign-list/Listing';
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { useGetAuthenticatedUserQuery } from './redux/services/folderName';
import { setBannerData, setInventoryUsage, setMasterData, setUserData as setUserDataRedux, setUserDetails } from './redux/slice/campaignSlice';
import { useDispatch } from 'react-redux';
import { LoginErrorResponse, RTKErrorType } from './types/apiResponses';
import { ChannelInfo, getChannelId, getGtmIdForSiteContext } from './utils/helpers';
import CampaignCreationV2 from './features/campaignCreationv2/CampaignCreationV2';
import { userActions } from './redux/slice/userSlice';
import HeaderLayout from './HeaderLayout';
import { setIsLoginSuccess } from './redux/slice/login.slice';
import { getUserDetails } from './utils/utils';
import ConfigureMail from './features/configureMail/ConfigureMail';
import { useInventoryUsageMutation } from './redux/services/campaignListingService';
import { MAX_COUNT } from './utils/whatsAppUtils';
import { useLazyCheckRevampServicesQuery } from './redux/services/userService';


interface OutreachBannerConfig {
  level: string;
  isExhausted: boolean;
  inventoryLevel: string;
}

export function getOutreachBannerMessage({
  level,
  isExhausted,
  inventoryLevel,
}: OutreachBannerConfig) {
  const isService = level === "service";
  const isRecruiter = level === "recruiter";
  const isFullyExhausted = inventoryLevel === "total" && isExhausted;

   if (isService) {
     return {
       message: (
         <div>
           Your email outreach service has expired. Kindly, connect with CS to
           upgrade plan
         </div>
       ),
       cta: "Upgrade limit",
     };
   }


  if (isRecruiter) {
    // Recruiter level
    return {
      message: isExhausted ? ( // 100% used
        <>
          {isFullyExhausted ? (
            <div>
              Your recruiter email outreach credits has been{" "}
              <span className="bold-600">fully exhausted</span>
            </div>
          ) : (
            <div>
              You&apos;ve exhausted your{" "}
              <span className="bold-600">{inventoryLevel}</span> recruiter
              email outreach credits
            </div>
          )}
        </>
      ) : (
        // 80% used
        <div>
          You&apos;ve used <span className="bold-600">80%</span> of your{" "}
          <span className="bold-600">{inventoryLevel}</span> recruiter email outreach
          credits
        </div>
      ),
      cta: "Upgrade limit",
    };
  }

  return {
    // Account level
    message: isExhausted ? ( // 100% used
      <>
        {isFullyExhausted ? (
          <div>
            Your account has <span className="bold-600">fully exhausted</span> email outreach credits
          </div>
        ) : (
          <div>
            You&apos;ve exhausted your{" "}
            <span className="bold-600">{inventoryLevel}</span> account level
            email outreach credits
          </div>
        )}
      </>
    ) : (
      // 80% used
      <div>
        Your account has used <span className="bold-600">80%</span> of its{" "}
        <span className="bold-600">{inventoryLevel}</span> email outreach credits
      </div>
    ),
    cta: "Upgrade limit",
  };
}

function App() {
  const isOnline = useOnlineStatus();
  const bannerRef:any = useRef(null);
  const [userData, setUserData] = useState<any>(null);
  // const [triggerServiceCheck, { isError, isSuccess, data: revampData }] =
  //   useLazyCheckRevampServicesQuery();


  // const { data: userData, error, isLoading } = useGetAuthenticatedUserQuery(1);
  const dispatch = useDispatch();
  const [hasDisconnectedOnce, setHasDisconnectedOnce] = useState<boolean>(false);
  const [toastValue, setToastValue] = useState<Toast>({
    isToastShown: false,
    toastMessage: '',
    toastKey: '',
    toastSeverity: undefined,
    action: <div></div>
  })
  const [getInventoryUsage ] = useInventoryUsageMutation();

  const onToastClose = () => {
    setToastValue({ ...toastValue, isToastShown: false })
  }

  const fetchUserDetails = () => {
    const details = getUserDetails();
    if (details && Object.keys(details).length > 0) {
      setUserData(details);
    }
  };

   const searchParams = new URLSearchParams(window.location.search);

  useEffect(() => {
    if (!userData) {
      const intervalId = setInterval(() => {
        fetchUserDetails();
      }, 100);
  
      return () => clearInterval(intervalId);
    }
  }, [userData]);

  // useEffect(()=>{
  //   if(userData && userData.corp_id){
  //     triggerServiceCheck(Number(userData.corp_id));
  //   }
  // },[triggerServiceCheck, userData]);

  // useEffect(()=>{
  //   if(isSuccess && revampData){
  //     // temporary changes
  //     const validUsers = ["xshruthix02", "xrajattiwarix01", "xbridgex01"];

  //     if(validUsers?.includes(userData.username?.toLowerCase() || "")){
  //       dispatch(userActions.setCampaignMaxLimit({ campaignMaxLimit:"7500" }));
  //     }

  //     // ===> Correct changes - commented for now
  //     // const campaignMaxLimit = revampData.outreachCampaignLimit;
      
  //     // if(campaignMaxLimit){
  //     //   dispatch(userActions.setCampaignMaxLimit({ campaignMaxLimit }));
  //     // }
  //   }
  // },[dispatch, isSuccess, revampData, userData]);

  useEffect(() => {
    if(!userData || !userData.site_context) return;

    // GTM ID
    const gtmId = getGtmIdForSiteContext(userData.site_context);

    // Add the GTM script to the head
    const script = document.createElement('script');
    script.text = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer', '${gtmId}');
    `;
    document.head.appendChild(script);

    // Add the noscript iframe to the body
    const noScriptElement = document.createElement('noscript');
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
    noScriptElement.appendChild(iframe);
    document.body.insertBefore(noScriptElement, document.body.firstChild);
  }, [userData]);

  useEffect(() => {
    if (!isOnline) {
      setToastValue({
        isToastShown: true,
        toastMessage: 'Network disconnected',
        toastKey: 'Error-network-disconnected',
        toastSeverity: 'error'
      })
      setHasDisconnectedOnce(true);
    } else {
      if (hasDisconnectedOnce) {
        setToastValue({
          isToastShown: true,
          toastMessage: 'Back Online',
          toastKey: 'Success-back-online',
          toastSeverity: 'success'
        })
      }
    }
  }, [isOnline]);

  useEffect(() => {
    // if(error){
    //   const url = window.location.href;
    //   const loginUrl = (error as RTKErrorType<LoginErrorResponse>)?.data?.info?.redirect?.log_in_url;
    //   const redirectUrl = loginUrl + "?src=" + encodeURIComponent(url);
    //   window.location.href = redirectUrl;
    // }
    if(userData){
      let requiredUserData = {
        recruiterId: userData.recruiter_id,
        channelId: userData.channel_id,
        corpId: userData.corp_id,
        subChannelId: userData.subhchannel_id,
        username: userData.username,
        trackingId: userData.tracking_id,
        siteContext: userData.site_context,
        campaignMaxLimit: `${MAX_COUNT}`
      }
     
      dispatch(setUserDataRedux({ userData:  requiredUserData}));
      dispatch(setIsLoginSuccess(true));
      dispatch(setUserDetails({ userDetails:  userData}));
      dispatch(userActions.setUser({userData: requiredUserData}));
    }
    if(userData || searchParams){
 const cn = searchParams.get('cn') as string
      const { channelId } : ChannelInfo = getChannelId(cn)
      const subChannelId = Number(userData?.subhchannel_id) >= (channelId ?? userData?.channel_id ) ? Number(userData?.subhchannel_id): channelId
      const masterDataQuery = {
        channelId:channelId || userData?.channel_id,
        subChannelId: subChannelId || userData?.subhchannel_id,
        recruiterId: userData?.recruiter_id,
      }
      dispatch(setMasterData({masterData: masterDataQuery }))
    }
  },[userData, dispatch,searchParams])

useEffect(() => {
  if (!userData) return;

  const fetchInventoryUsage = async () => {
    try {
      const data = await getInventoryUsage({
        channel_id: userData.channel_id,
        corp_id: userData.corp_id,
        recruiter_id: userData.recruiter_id,
        subChannel_id: userData.subChannel_id,
        type: userData?.account_type === "purchase" ? "OS_EMAIL" : "EM",
      }).unwrap();

      const banner = getOutreachBannerMessage({
        inventoryLevel: data.inventoryLevel, 
        isExhausted: data.isExhausted,
        level: data.level,
      });

      if(banner){
        dispatch(setBannerData({bannerData: [banner],}));
      }
      dispatch(setInventoryUsage({inventoryUsage: data}))
    } catch (error) {
      // console.error("Failed to fetch inventory usage", error);
    }
  };

  fetchInventoryUsage();
}, [dispatch, getInventoryUsage, userData]);



  return (
    <ThemeProvider theme={theme}>
      <ToastContext.Provider value={{ setToastValue }}>
        <BrowserRouter>
          <HeaderLayout />
          <Routes>
            <Route path='/edge/recruiter-outreach-email/campaing/creation' element={<Layout isLoading={false} recruiterId={userData?.recruiter_id || ""} bannerRef={bannerRef}/>} />
            <Route path='/edge/recruiter-outreach-email/campaing/listing' element={<Listing isLoading={false} bannerRef={bannerRef}/>} />
            <Route path='/edge/recruiter-outreach-email/campaign/creation'>
              <Route index path='whatsApp' element={<CampaignCreationV2 campaignType="whatsApp" />} />
            </Route>
            <Route path='/edge/recruiter-outreach-email/configureMail' element={<ConfigureMail />} />
          </Routes>
        </BrowserRouter>
      </ToastContext.Provider>
      <CustomToast
        isToastShown={toastValue.isToastShown}
        onToastClose={onToastClose}
        toastMessage={toastValue.toastMessage}
        toastKey={toastValue.toastKey}
        toastSeverity={toastValue.toastSeverity}
        action={toastValue.action}
        autoHideDuration={toastValue.autoHideDuration}
      />
    </ThemeProvider >
  );
}

export default App;
